import { defaultSurveyButtonConfig } from "./survey.constants";

export function snakeValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function questionFromApi(question) {
  const options = question.options || [];
  const other = options.find((option) => option.allowsText);
  const regularOptions = options
    .filter((option) => !option.allowsText)
    .map((option) => ({ id: option.id, label: option.label, order: option.order }));
  const value = {
    ...question,
    description: question.description || "",
    required: typeof question.required === "boolean" ? question.required : true,
  };
  if (question.type === "single_choice" || question.type === "multi_choice") {
    value.options = regularOptions;
    value.allowOther = Boolean(other);
    value.otherLabel = other ? other.label || "Other" : "Other";
    value.otherPlaceholder = other ? other.textPlaceholder || "" : "";
  } else if (question.type === "text") {
    value.inputStyle = question.inputStyle || "multi_line";
    value.placeholder = question.placeholder || "";
  } else if (question.type === "number") {
    value.placeholder = question.placeholder || "";
    value.unit = question.unit || "";
    value.validation = {
      min: question.validation && typeof question.validation.min !== "undefined" ? question.validation.min : "",
      max: question.validation && typeof question.validation.max !== "undefined" ? question.validation.max : "",
      step: question.validation && typeof question.validation.step !== "undefined" ? question.validation.step : 1,
    };
  } else if (question.type === "rating") {
    value.ratingStyle = question.ratingStyle || "stars";
    value.min = 1;
    value.max = 5;
    value.minLabel = question.minLabel || "";
    value.maxLabel = question.maxLabel || "";
  }
  return value;
}

export function surveyToForm(survey) {
  return {
    id: null,
    title: "",
    description: "",
    status: "draft",
    targetingType: 1,
    testLeadCount: 0,
    reward: null,
    buttonConfig: { ...defaultSurveyButtonConfig },
    questions: [],
    ...survey,
    description: survey.description || "",
    targetingType: Number(survey.targetingType || 1),
    reward: survey.reward
      ? { type: survey.reward.type, amount: survey.reward.amount }
      : null,
    buttonConfig: survey.buttonConfig
      ? {
          label: survey.buttonConfig.label || "",
          deepLinkUrl: survey.buttonConfig.deepLinkUrl || "",
        }
      : null,
    questions: (survey.questions || []).map(questionFromApi),
  };
}

export function duplicateSurveyForm(form) {
  return {
    ...form,
    id: null,
    title: `${String(form.title || "").trim()} (Copy)`,
    status: "draft",
    isActive: true,
    testLeadCount: 0,
    submissionCount: 0,
    publishedAt: null,
    createdAt: null,
    updatedAt: null,
    reward: form.reward ? { ...form.reward } : null,
    buttonConfig: form.buttonConfig ? { ...form.buttonConfig } : null,
    questions: JSON.parse(JSON.stringify(form.questions || [])),
  };
}

function optionalText(target, key, value) {
  const normalized = String(value || "").trim();
  if (normalized) target[key] = normalized;
}

export function surveyPayload(form) {
  return {
    title: String(form.title || "").trim(),
    description: String(form.description || "").trim(),
    targetingType: Number(form.targetingType || 1),
    reward: form.reward
      ? { type: form.reward.type, amount: Number(form.reward.amount) }
      : null,
    buttonConfig: form.buttonConfig
      ? {
          label: String(form.buttonConfig.label || "").trim(),
          deepLinkUrl: String(form.buttonConfig.deepLinkUrl || "").trim(),
        }
      : null,
    questions: (form.questions || []).map((question, questionIndex) => {
      const value = {
        id: question.id,
        type: question.type,
        question: String(question.question || "").trim(),
        required: Boolean(question.required),
        order: questionIndex + 1,
      };
      optionalText(value, "description", question.description);
      if (question.type === "single_choice" || question.type === "multi_choice") {
        value.options = (question.options || []).map((option, optionIndex) => ({
          id: option.id,
          label: String(option.label || "").trim(),
          value: snakeValue(option.label),
          order: optionIndex + 1,
        }));
        if (question.type === "single_choice" && question.allowOther) {
          value.options.push({
            id: "other",
            label: String(question.otherLabel || "").trim(),
            value: "other",
            order: value.options.length + 1,
            allowsText: true,
            textPlaceholder: String(question.otherPlaceholder || "").trim(),
          });
        }
      } else if (question.type === "text") {
        value.inputStyle = question.inputStyle || "multi_line";
        optionalText(value, "placeholder", question.placeholder);
      } else if (question.type === "number") {
        optionalText(value, "placeholder", question.placeholder);
        optionalText(value, "unit", question.unit);
        const validation = question.validation || {};
        value.validation = { step: Number(validation.step) };
        if (typeof validation.min !== "undefined" && validation.min !== "" && validation.min !== null) {
          value.validation.min = Number(validation.min);
        }
        if (typeof validation.max !== "undefined" && validation.max !== "" && validation.max !== null) {
          value.validation.max = Number(validation.max);
        }
      } else if (question.type === "rating") {
        value.ratingStyle = question.ratingStyle || "stars";
        value.min = 1;
        value.max = 5;
        optionalText(value, "minLabel", question.minLabel);
        optionalText(value, "maxLabel", question.maxLabel);
      }
      return value;
    }),
  };
}
